"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface Post {
  id: number;
  title: string;
  description: string;
  date: string;
  category: "research" | "awards" | "collaboration" | "student spotlight" | "student achievement" | "software release" | "workshop" | string;
  images: string[];
}
interface PostCardProps {
  post: Post;
}

const getCategoryColor = (category?: string | null) => {
  if (!category) {
    return 'bg-gray-100 text-gray-800';
  }

  switch (category.toLowerCase()) {
    case 'research':
      return 'bg-blue-100 text-blue-800';
    case 'awards':
      return 'bg-yellow-100 text-yellow-800';
    case 'collaboration':
      return 'bg-green-100 text-green-800';
    case 'student spotlight':
    case 'student achievement':
      return 'bg-purple-100 text-purple-800';
    case 'software release':
      return 'bg-indigo-100 text-indigo-800';
    case 'workshop':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};


export default function PostCard({ post }: PostCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className="h-full flex flex-col hover:shadow-lg transition-shadow duration-200">
  
      <CardHeader>
        <div className="flex justify-between items-start mb-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
            {post.category}
          </span>
          <span className="text-sm text-gray-500">{post.date}</span>
        </div>
        <CardTitle className="text-lg leading-tight">{post.title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {post.description.length > 150 
            ? `${post.description.slice(0, 150)}...` 
            : post.description}
        </p>
        <div className="mt-auto">
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" className="w-full">
                Read More
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex justify-between items-start mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                    {post.category}
                  </span>
                  <span className="text-sm text-gray-500">{post.date}</span>
                </div>
                <DialogTitle className="text-2xl leading-tight pr-6">
                  {post.title}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
              {post.images?.length > 0 && (
  <div className="mb-6">
    <h3 className="font-semibold text-lg mb-3">Photos</h3>

    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {post.images.map((img, i) => (
        <img
          key={i}
          src={img}
          alt="post image"
          className="rounded-lg object-cover w-full h-80"
        />
      ))}
    </div>
  </div>
)}
                <div className="max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {post.description}
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}
